import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import back_app from '../../lib/back_app';
import CreatorHeader from '../../components/creator/CreatorHeader';

import xdate from '../../../../../util/date.js';

type Video = {
  id: string;
  title: string;
  video_url?: string;
  thumbnail_url?: string;
  ts?: {insert: Date, modified: Date};
  status: string;
  duration: number;
};

type VideoCampaign = {
  id: string;
  video_id: string;
  campaign_id: string;
  processing_status: string;
  ts?: {insert: Date, modified: Date};
  brand_name?: string;
  campaigns?: {
    id: string;
    name: string;
    category: string;
  };
  videos?: {
    id: string;
    title: string;
    thumbnail_url?: string;
    video_url: string;
  };
};

type Stats = {
  totalVideos: number;
  activeCampaigns: number;
  completedCampaigns: number;
  processingCampaigns: number;
};

function VideoCard({ video, onClick }: { video: Video; onClick: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className="bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-lg transition-all cursor-pointer"
      onClick={onClick}
    >
      {}
      <div
        className="relative bg-gradient-to-br from-gray-900 to-gray-700 overflow-hidden group"
        style={{ aspectRatio: '9/16' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {video.video_url ? (
          <>
            <video
              ref={videoRef}
              src={video.video_url}
              className="w-full h-full object-cover"
              muted
              loop
            />
            {}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
              <div className="w-12 h-12 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                </svg>
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-white text-4xl">🎬</div>
          </div>
        )}

        {}
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
            video.status === 'published' ? 'bg-green-500 text-white' :
            video.status === 'processing' ? 'bg-yellow-500 text-white' :
            'bg-gray-500 text-white'
          }`}>
            {video.status}
          </span>
        </div>

        {}
        {video.duration > 0 && (
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 px-2 py-1 rounded text-white text-xs font-semibold">
            {Math.floor(video.duration / 60)}:{String(Math.floor(video.duration % 60)).padStart(2, '0')}
          </div>
        )}
      </div>

      {}
      <div className="p-3">
        <h3 className="font-bold text-sm text-gray-900 line-clamp-2 mb-1">
          {video.title}
        </h3>
        <p className="text-xs text-gray-500">
          {video.ts?.insert ? xdate.fmt_lbl_short(video.ts.insert) : ''}
        </p>
      </div>
    </div>
  );
}

function CampaignCard({ campaign, onClick }: { campaign: VideoCampaign; onClick: () => void }) {
  return (
    <div
      className="bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-lg transition-all cursor-pointer"
      onClick={onClick}
    >
      {}
      <div className="relative bg-gradient-to-br from-gray-900 to-gray-700 overflow-hidden group" style={{ aspectRatio: '9/16' }}>
        {campaign.videos?.thumbnail_url || campaign.videos?.video_url ? (
          <>
            {campaign.videos.thumbnail_url ? (
              <img
                src={campaign.videos.thumbnail_url}
                alt={campaign.videos.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <video
                src={campaign.videos.video_url}
                className="w-full h-full object-cover"
                muted
              />
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-white text-4xl">🎬</div>
          </div>
        )}

        {}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
          <div className="w-12 h-12 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-600 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
            </svg>
          </div>
        </div>

        {}
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
            campaign.processing_status === 'completed' ? 'bg-green-500 text-white' :
            campaign.processing_status === 'processing' ? 'bg-yellow-500 text-white' :
            campaign.processing_status === 'analyzing' ? 'bg-blue-500 text-white' :
            'bg-gray-500 text-white'
          }`}>
            {campaign.processing_status === 'completed' ? '✓' :
             campaign.processing_status === 'processing' ? '⏳' :
             campaign.processing_status === 'analyzing' ? '🤖' :
             campaign.processing_status}
          </span>
        </div>
      </div>

      {}
      <div className="p-3">
        {campaign.brand_name && (
          <p className="text-xs text-gray-500 mb-0.5">
            {campaign.brand_name}
          </p>
        )}
        <h3 className="font-bold text-sm text-gray-900 line-clamp-1 mb-0.5">
          {campaign.campaigns?.name || 'Campaign'}
        </h3>
        <p className="text-xs text-gray-500">
          {campaign.ts?.insert ? xdate.fmt_lbl_short(campaign.ts.insert) : ''}
        </p>
      </div>
    </div>
  );
}

export default function CreatorDashboard() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState<Video[]>([]);
  const [campaigns, setCampaigns] = useState<VideoCampaign[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalVideos: 0,
    activeCampaigns: 0,
    completedCampaigns: 0,
    processingCampaigns: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const videosResult = await back_app.video_list();
      const campaignsResult = await back_app.vcamp_list();

      if (!videosResult.err) {
        const videosData = videosResult || [];
        setVideos(videosData);

        const campaignsData = !campaignsResult.err ? (campaignsResult || []) : [];
        setCampaigns(campaignsData);

        const completed = campaignsData.filter((c: VideoCampaign) => c.processing_status === 'completed').length;
        const processing = campaignsData.filter((c: VideoCampaign) => 
          c.processing_status === 'processing' || c.processing_status === 'analyzing'
        ).length;

        setStats({
          totalVideos: videosData.length,
          activeCampaigns: campaignsData.length,
          completedCampaigns: completed,
          processingCampaigns: processing,
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <CreatorHeader />

      {}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2">Creator Dashboard</h2>
              
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/creator/campaigns')}
                className="px-6 py-3 bg-white/10 backdrop-blur border-2 border-white/30 text-white rounded-lg font-semibold hover:bg-white/20 transition-all text-center"
              >
                Browse Campaigns
              </button>
              <button
                onClick={() => navigate('/creator/upload')}
                className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:shadow-lg transition-all text-center"
              >
                + Upload Video
              </button>
            </div>
          </div>
        </div>
      </div>

      <p className="text-white/90">Manage your videos and campaigns</p>

      {}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-6 sm:-mt-8 mb-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"/>
                </svg>
              </div>
            </div>
            <div className="text-gray-600 text-xs sm:text-sm mb-1">Total Videos</div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.totalVideos}</div>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"/>
                </svg>
              </div>
            </div>
            <div className="text-gray-600 text-xs sm:text-sm mb-1">Campaigns</div>
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.activeCampaigns}</div>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
            </div>
            <div className="text-gray-600 text-xs sm:text-sm mb-1">Completed</div>
            <div className="text-2xl sm:text-3xl font-bold text-green-600">{stats.completedCampaigns}</div>
          </div>

          <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                </svg>
              </div>
            </div>
            <div className="text-gray-600 text-xs sm:text-sm mb-1">Processing</div>
            <div className="text-2xl sm:text-3xl font-bold text-yellow-600">{stats.processingCampaigns}</div>
          </div>
        </div>
      </div>

      {}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 space-y-8">
        {}
        <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Recent Videos</h2>
            <button
              onClick={() => navigate('/creator/videos')}
              className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1"
            >
              <span>View All</span>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
              </svg>
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-600 mt-4">Loading...</p>
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎬</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No videos yet</h3>
              <p className="text-gray-600 mb-6">Upload your first video to start earning</p>
              <button
                onClick={() => navigate('/creator/upload')}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Upload First Video
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {videos.slice(0, 6).map((video) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  onClick={() => navigate(`/creator/videos/${video.id}`)}
                />
              ))}
            </div>
          )}
        </div>

        {}
        {campaigns.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Recent Campaigns</h2>
              <button
                onClick={() => navigate('/creator/my-campaigns')}
                className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center gap-1"
              >
                <span>View All</span>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {campaigns.slice(0, 6).map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  onClick={() => navigate(`/creator/video-processing?id=${campaign.id}&video_id=${campaign.video_id}&campaign_id=${campaign.campaign_id}`)}
                />
              ))}
            </div>
          </div>
        )}

        {}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div 
            onClick={() => navigate('/creator/campaigns')}
            className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-1">Join New Campaign</h3>
                <p className="text-sm text-gray-600">Browse and join campaigns to monetize your content</p>
              </div>
            </div>
          </div>

          <div 
            onClick={() => navigate('/creator/upload')}
            className="bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 rounded-xl p-6 hover:shadow-lg transition-all cursor-pointer group"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z"/>
                  <path d="M9 13h2v5a1 1 0 11-2 0v-5z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-1">Upload New Video</h3>
                <p className="text-sm text-gray-600">Add more content to expand your reach</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}